import { useCallback, useMemo } from 'react';

import { ApiMethod } from '../models/api-types.type';
import { useAuthContext } from './use-auth-context.hook';

/**
 * Being found by AI assistants (feat/room-to-revenue, workstream E): the
 * switch that lets ChatGPT, Claude & co. use the catalogue through the MCP
 * server, the feed and llms.txt, and the AI-visibility checks.
 * API: pms-api-v2 src/pms/pms-agent-commerce.
 */

export const AGENT_TOOL_NAMES = ['search_products', 'get_product', 'see_in_my_room', 'find_store', 'request_sample'] as const;
export type AgentToolName = (typeof AGENT_TOOL_NAMES)[number];

export interface AgentCommerceSettings {
    enabled: boolean;
    tools: AgentToolName[];
    allTools: AgentToolName[];
    publicName: string;
    description: string;
    visibility: { weekly: boolean };
    enabledAt: string | null;
    updatedAt: string | null;
    defaults: { publicName: string; description: string };
    alias: string;
    urls: { mcp: string; platformMcp: string; feed: string; llms: string; visualizer: string; upload: string };
    catalogue: { products: number; priced: number; stores: number; storesLocated: number };
}

export interface AgentPrice {
    amount: number;
    currency: string;
    unit?: string;
    compareAt?: number;
    compareAtLabel?: string;
    label: string;
    source: string;
}

export interface AgentProductCard {
    id: string;
    sku?: string;
    name: string;
    brand?: string;
    image?: string;
    price: AgentPrice | null;
    size?: string;
    colour?: string;
    material?: string;
    finish?: string;
    surfaces?: string[];
    productPageUrl?: string;
    seeInRoomUrl: string;
    url: string;
    matched?: string[];
}

export interface McpToolCallResult {
    content: Array<{ type: 'text'; text: string }>;
    structuredContent?: Record<string, any>;
    isError?: boolean;
}

export interface TryToolResponse {
    request: Record<string, unknown>;
    response: { jsonrpc: '2.0'; id: number; result?: McpToolCallResult; error?: { code: number; message: string } };
}

export interface VisibilitySource {
    url: string;
    domain: string;
    title?: string;
}

export interface VisibilityRow {
    promptId: string;
    prompt: string;
    engine: string;
    engineLabel: string;
    model: string;
    mentioned: boolean;
    cited: boolean;
    position: number | null;
    competitors: string[];
    sources: VisibilitySource[];
    excerpt?: string;
    costUsd?: number;
    error?: string;
}

export interface VisibilityPrompt {
    id: string;
    text: string;
    category?: string;
    place?: string;
    lang: string;
}

export interface VisibilityCheck {
    uuid: string;
    status: 'running' | 'done' | 'failed';
    trigger: 'manual' | 'weekly' | 'seed';
    subject: { name: string; aliases: string[]; domains: string[] };
    engines: Array<{ id: string; label: string; model: string }>;
    prompts: VisibilityPrompt[];
    results: VisibilityRow[];
    score?: number | null;
    mentionRate?: number | null;
    citedRate?: number | null;
    averagePosition?: number | null;
    costUsd: number;
    budgetUsd?: number;
    skipped: number;
    error?: string;
    startedAt?: string;
    finishedAt?: string;
    createdAt?: string;
}

export interface VisibilityLatest {
    check: VisibilityCheck | null;
    lastDone: VisibilityCheck | null;
    plan: { prompts: VisibilityPrompt[]; engines: Array<{ id: string; label: string; model: string }>; budgetUsd: number };
    weekly: boolean;
}

export interface VisibilityHistoryPoint {
    uuid: string;
    at: string;
    trigger: string;
    score: number | null;
    mentionRate: number | null;
    citedRate: number | null;
    averagePosition: number | null;
    answers: number;
    costUsd: number;
}

const BASE = '/services/pms/agent-commerce';
const VISIBILITY = '/services/pms/ai-visibility';

export const useAiChannelsApi = () => {
    const { sendAuthGuardedRequest } = useAuthContext();

    const getSettings = useCallback(
        async (): Promise<AgentCommerceSettings> => (await sendAuthGuardedRequest(ApiMethod.GET, `${BASE}/settings`)) as AgentCommerceSettings,
        [sendAuthGuardedRequest],
    );

    const updateSettings = useCallback(
        async (patch: Partial<Pick<AgentCommerceSettings, 'enabled' | 'tools' | 'publicName' | 'description' | 'visibility'>>): Promise<AgentCommerceSettings> =>
            (await sendAuthGuardedRequest(ApiMethod.PUT, `${BASE}/settings`, patch)) as AgentCommerceSettings,
        [sendAuthGuardedRequest],
    );

    /** Runs a read-only tool exactly as an assistant would — works before the switch is on. */
    const tryTool = useCallback(
        async (tool: Exclude<AgentToolName, 'request_sample'>, args: Record<string, unknown>): Promise<TryToolResponse> =>
            (await sendAuthGuardedRequest(ApiMethod.POST, `${BASE}/try`, { tool, arguments: args })) as TryToolResponse,
        [sendAuthGuardedRequest],
    );

    const runVisibility = useCallback(
        async (): Promise<{ uuid: string; status: string; prompts: number; engines: number }> =>
            (await sendAuthGuardedRequest(ApiMethod.POST, `${VISIBILITY}/run`, {})) as { uuid: string; status: string; prompts: number; engines: number },
        [sendAuthGuardedRequest],
    );

    const latestVisibility = useCallback(
        async (): Promise<VisibilityLatest> => (await sendAuthGuardedRequest(ApiMethod.GET, `${VISIBILITY}/latest`)) as VisibilityLatest,
        [sendAuthGuardedRequest],
    );

    const visibilityHistory = useCallback(
        async (): Promise<VisibilityHistoryPoint[]> => (await sendAuthGuardedRequest(ApiMethod.GET, `${VISIBILITY}/history`)) as VisibilityHistoryPoint[],
        [sendAuthGuardedRequest],
    );

    return useMemo(
        () => ({ getSettings, updateSettings, tryTool, runVisibility, latestVisibility, visibilityHistory }),
        [getSettings, updateSettings, tryTool, runVisibility, latestVisibility, visibilityHistory],
    );
};

/** A public page (llms.txt, the feed) as text, for the preview — no credentials needed or sent. */
export async function fetchPublicText(url: string, maxChars = 4000): Promise<string> {
    const res = await fetch(url, { credentials: 'omit' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const text = await res.text();
    return text.length > maxChars ? `${text.slice(0, maxChars)}\n…` : text;
}
